import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalRoot,
  TextInput,
} from '@pella/sash-framework';

/**
 * Cancellation reasons constants
 */
export const FIXED_BY_SUBMITTER = 'FIXED_BY_SUBMITTER';
export const TESTING = 'TESTING';
export const OTHER = 'OTHER';

export const cancellationReasons = {
  FIXED_BY_SUBMITTER,
  TESTING,
  OTHER,
};

const reasonOptions = [
  { value: FIXED_BY_SUBMITTER, labelKey: 'reasonFixedProblem' },
  { value: TESTING, labelKey: 'reasonTrainingTesting' },
  { value: OTHER, labelKey: 'reasonOther' },
];

/**
 * Dialog for selecting a cancellation reason.
 * Allows users to select from predefined reasons and optionally provide additional details.
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {() => void} props.onClose - Callback when dialog is closed
 * @param {(reason: string, details: string) => void} props.onConfirm - Callback when user confirms selection with parameters (reason, details)
 * @returns {import("react").ReactElement} Cancellation reason dialog component
 */
const CancellationReasonDialog = ({ isOpen, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');

  const handleReasonChange = (event) => {
    const value = event.target.value;
    setSelectedReason(value);
    if (value !== OTHER) setAdditionalDetails('');
  };

  const handleConfirm = () => {
    // The OK button is disabled when selectedReason is empty, so the else path
    // is unreachable through normal UI interaction — guard exists for safety.
    /* istanbul ignore else */
    if (selectedReason) {
      onConfirm(selectedReason, additionalDetails);
      setSelectedReason('');
      setAdditionalDetails('');
    }
  };

  const handleClose = () => {
    onClose();
    setSelectedReason('');
    setAdditionalDetails('');
  };

  return (
    <ModalRoot open={isOpen} onOpenChange={handleClose}>
      <Modal>
        <ModalHeader>{t('selectClosureReasonTitle')}</ModalHeader>
        <ModalContent>
          <p>{t('selectClosureReasonParagraph')}</p>
          <Select
            id='cancellation-reason-select'
            labelText={t('reasonLabel')}
            value={selectedReason}
            onChange={handleReasonChange}
          >
            <option value=''>{t('notSelected')}</option>
            {reasonOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Select>
          {selectedReason === OTHER && (
            <TextInput
              id='additional-details'
              labelText={t('additionalDetails')}
              value={additionalDetails}
              onChange={(e) => setAdditionalDetails(e.target.value)}
            />
          )}
        </ModalContent>
        <ModalFooter>
          <Button size='sm' color='secondary' onClick={handleClose}>
            {t('cancel')}
          </Button>
          <Button size='sm' color='primary' onClick={handleConfirm} disabled={!selectedReason}>
            {t('okButton')}
          </Button>
        </ModalFooter>
      </Modal>
    </ModalRoot>
  );
};

export default CancellationReasonDialog;
